import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { get_recording_auto_analyze, put_recording_auto_analyze } from "../api";
import { frontendCapabilities } from "../globals";
import { SupportedLanguage, isValidLanguage, supportedLanguages } from "../globals/i18n";
import { reportThemeToShell } from "./desktopShell";

type Theme = "light" | "dark";

const THEME_STORAGE_KEY = "clank.theme";
const LANG_STORAGE_KEY = "clank.language";

const readStoredTheme = (): Theme => {
  if (typeof window === "undefined") return "light";
  const storedValue = window.localStorage.getItem(THEME_STORAGE_KEY);
  if (storedValue === null) return "light";
  if (storedValue === "dark" || storedValue === "light") return storedValue;
  window.localStorage.removeItem(THEME_STORAGE_KEY);
  return "light";
};

const readStoredLanguage = (): SupportedLanguage | null => {
  if (typeof window === "undefined") return null;
  const storedValue = window.localStorage.getItem(LANG_STORAGE_KEY);
  if (storedValue === null) return null;
  if (isValidLanguage(storedValue)) return storedValue;
  window.localStorage.removeItem(LANG_STORAGE_KEY);
  return null;
};

const i18nLangToSupportedLanguage: (
  val: ReturnType<typeof useTranslation>["i18n"]["language"],
) => SupportedLanguage = val => {
  for (const search of supportedLanguages.map(val => val.id)) {
    if (val.startsWith(search)) {
      return search;
    }
  }
  return "en";
};

const applyThemeClass = (theme: Theme) => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle("dark", theme === "dark");
  // The desktop shell paints the Windows caption strip itself and has to
  // follow along.
  reportThemeToShell(theme);
};

// Apply persisted theme at module load so the first paint matches the user's choice.
applyThemeClass(readStoredTheme());

type SettingsContextValue = {
  autoAnalyze: boolean;
  setAutoAnalyze: (v: boolean) => void;
  theme: Theme;
  setTheme: (v: Theme) => void;
  language: SupportedLanguage;
  setLanguage: (v: SupportedLanguage) => void;
};

type StoredSettingValue = string | boolean;

const usePersistedSetting = <T extends StoredSettingValue>(
  key: string,
  value: T,
  setValue: React.Dispatch<React.SetStateAction<T>>,
): ((v: T) => void) =>
  useCallback(
    (nextValue: T) => {
      if (nextValue === value) return;
      if (typeof window !== "undefined") {
        window.localStorage.setItem(key, String(nextValue));
      }
      setValue(nextValue);
    },
    [key, value, setValue],
  );

const getLanguage = (
  savedLang: SupportedLanguage | null,
  browserLang: string,
): SupportedLanguage => {
  if (savedLang !== null) return savedLang;
  if (isValidLanguage(browserLang)) {
    return browserLang as SupportedLanguage;
  }
  return "en";
};

const SettingsContext = createContext<SettingsContextValue | null>(null);

export const SettingsProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation();
  const [autoAnalyze, setAutoAnalyzeState] = useState(false);
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  const savedLang = readStoredLanguage();
  const browserLang = i18nLangToSupportedLanguage(i18n.language);
  const baseLang = getLanguage(savedLang, browserLang);
  const [language, setLanguageState] = useState<SupportedLanguage>(baseLang);

  useEffect(() => {
    applyThemeClass(theme);
  }, [theme]);

  useEffect(() => {
    if (!frontendCapabilities.localRecording) {
      return;
    }
    let cancelled = false;
    get_recording_auto_analyze()
      .then(payload => {
        if (!cancelled) {
          setAutoAnalyzeState(payload.auto_analyze_after_sync);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAutoAnalyzeState(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const setAutoAnalyze = useCallback(
    (nextValue: boolean) => {
      if (nextValue === autoAnalyze) return;
      setAutoAnalyzeState(nextValue);
      if (!frontendCapabilities.localRecording) return;
      void put_recording_auto_analyze(nextValue).catch(() => {
        setAutoAnalyzeState(autoAnalyze);
      });
    },
    [autoAnalyze],
  );
  const setTheme = usePersistedSetting(THEME_STORAGE_KEY, theme, setThemeState);
  const setLanguage = usePersistedSetting(LANG_STORAGE_KEY, language, setLanguageState);

  return (
    <SettingsContext.Provider
      value={{
        autoAnalyze,
        setAutoAnalyze,
        theme,
        setTheme,
        language,
        setLanguage,
      }}
    >
      {children}
    </SettingsContext.Provider>
  );
};

export const useSettings = () => {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
};
